import { storeToRefs } from 'pinia';

import { useTrackingUserType } from '@/composables/tracking/useTrackingUserType';
import { CustomerSegment } from '@/models/customerSegments';
import {
  EVENT,
  trackEvent,
  type PageTrackingParameters,
} from '@/services/tracking/GtmTrackingService';
import {
  getQualificationTrackingState,
  normalizeStringOrUndefined,
} from '@/services/tracking/trackingUtils';
import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { useLandingStore } from '@/stores/landing';
import type { LandingQualificationMode } from '@/views/LandingPage/useLandingFormSimplified';

// Analytics event strings below are owned by the data team (PKP-6084) and must not be renamed.
export type LandingModalSource = 'ppa_benefit_teaser' | 'bottomsheet';

export const SEGMENT_CTA_SOURCE: LandingModalSource = 'ppa_benefit_teaser';

export type LandingSubmitEntryPoint =
  'input_start_at_hero_banner' | `input_start_at_${LandingModalSource}`;

export type LandingSectionImpressionAction =
  | 'display_ppa_benefit_teaser'
  | 'display_ppa_steps_teaser'
  | 'display_ppa_services_teaser'
  | 'display_ppa_faq'
  | 'display_expert_advice';

type LandingExperimentTrackingParams = Partial<
  Pick<
    PageTrackingParameters,
    | 'h_event_action_007'
    | 'h_event_label_008'
    | 'h_listing_offer_type_117'
    | 'h_advertiser_type_040'
    | 'h_target_url_029'
  >
> & {
  h_event_action_007: string;
};

const SESSION_KEY_PREFIX = 'lp_exp_';

function trackOncePerSession(key: string, track: () => void): void {
  if (typeof sessionStorage === 'undefined') {
    track();
    return;
  }

  const sessionKey = `${SESSION_KEY_PREFIX}${key}`;
  if (sessionStorage.getItem(sessionKey)) {
    return;
  }

  sessionStorage.setItem(sessionKey, 'true');
  track();
}

export function useLandingPageExperimentTracking() {
  const featureFlagsStore = useFeatureFlagsStore();
  const landingStore = useLandingStore();
  const { customerSegment, offerType } = storeToRefs(landingStore);
  const { trackingUserType } = useTrackingUserType();

  function track(
    event: typeof EVENT.INSERTION_INTERACTION | typeof EVENT.FEATURE_DISPLAYED,
    params: LandingExperimentTrackingParams,
  ) {
    if (!featureFlagsStore.isNewLandingPageTreatment) return;

    trackEvent({
      event,
      h_experiment_split_treatments_107: featureFlagsStore.getTrackingValue(),
      h_client_login_user_type_037: trackingUserType.value,
      ...params,
    });
  }

  function getCurrentQualificationState() {
    return getQualificationTrackingState(customerSegment.value, offerType.value);
  }

  function trackSelectListingType() {
    const { offerTypeNormalized, advertiserType } = getCurrentQualificationState();

    track(EVENT.INSERTION_INTERACTION, {
      h_event_action_007: 'select_listing_type',
      h_event_label_008: undefined,
      h_listing_offer_type_117: offerTypeNormalized,
      h_advertiser_type_040: advertiserType,
    });
  }

  function trackSelectAdvertiserType() {
    const { offerTypeNormalized, advertiserType } = getCurrentQualificationState();

    track(EVENT.INSERTION_INTERACTION, {
      h_event_action_007: 'select_advertiser_type',
      h_event_label_008: undefined,
      h_listing_offer_type_117: offerTypeNormalized,
      h_advertiser_type_040: advertiserType,
    });
  }

  function trackSwitchSegment(segment: CustomerSegment) {
    const { offerTypeNormalized } = getCurrentQualificationState();

    track(EVENT.INSERTION_INTERACTION, {
      h_event_action_007: `switch_to_${segment}_persona`,
      h_event_label_008: undefined,
      h_listing_offer_type_117: offerTypeNormalized,
      h_advertiser_type_040: normalizeStringOrUndefined(segment),
    });
  }

  function trackQualificationModalDisplayed(
    step: Exclude<LandingQualificationMode, 'none'>,
    source: LandingModalSource,
  ) {
    const isOfferTypeStep = step === 'offer-type';

    track(EVENT.FEATURE_DISPLAYED, {
      h_event_action_007: isOfferTypeStep
        ? 'display_listing_offer_type_modal'
        : 'display_advertiser_type_modal',
      h_event_label_008: source,
      h_listing_offer_type_117: isOfferTypeStep
        ? undefined
        : normalizeStringOrUndefined(offerType.value ?? undefined),
      h_advertiser_type_040: isOfferTypeStep
        ? normalizeStringOrUndefined(landingStore.stagedCustomerSegment ?? undefined)
        : undefined,
    });
  }

  function trackSectionImpression(action: LandingSectionImpressionAction, label?: string) {
    const sessionKey = `${action}_${label ?? 'default'}`;

    trackOncePerSession(sessionKey, () => {
      track(EVENT.FEATURE_DISPLAYED, {
        h_event_action_007: action,
        h_event_label_008: label,
      });
    });
  }

  function trackOpenFaq(trackingLabel: string) {
    track(EVENT.INSERTION_INTERACTION, {
      h_event_action_007: 'open_ppa_faq',
      h_event_label_008: trackingLabel,
    });
  }

  function trackOpenExpertAdvice(url: string) {
    track(EVENT.INSERTION_INTERACTION, {
      h_event_action_007: 'open_expert_advice',
      h_event_label_008: undefined,
      h_target_url_029: url,
    });
  }

  return {
    trackSelectListingType,
    trackSelectAdvertiserType,
    trackSwitchSegment,
    trackQualificationModalDisplayed,
    trackSectionImpression,
    trackOpenFaq,
    trackOpenExpertAdvice,
  };
}
